import colors from 'chalk'
import inquirer from 'inquirer'
import path from 'path'
import fs from 'fs-extra'
import { resetEffects, infoMessage, clearTerminal, warnMessage, symbols } from './terminal-utils'

const LINE_COL_RX = createFileLineRX()
const FILE_RX = /((\.\.\/|\.\/|\/)\S+)/mg
const MOD_NOT_FOUND = new RegExp("Can\'t resolve \'([^\' ]+)\'")

function createFileLineRX () {
    let colorIn = '(\\[\\d+m)?'
    let colorOut = '(\\[)?'
    let parts = [
        // 21:13
        // 21 13
        // 21 : 13
        // 21,13
        // also support colors from terminal
        `(${colorIn}\\d+[\\, \\:]+\\d+${colorOut})`,
        // line: 21 col: 13
        // line 21 col 13
        // line: 21, column: 13
        `(line[ \\:]+(\\d+)[\\n \\t\\/\\|\:\,\.]+col(umn)?[ \\:]+(\\d+))`,
        // \n 21\t13\n
        `(${colorIn}(^|[\\n\\t ])${colorIn}\\d+[ \\,\\|\\:]+\\d+${colorOut}[ \\t\\n]${colorOut})`
    ]
    return new RegExp(`${parts.join('|')}`, 'igm')
}

function reset () {
    return resetEffects
}

function treatStack (stack) {
    let traceLine = ''
    let trace = stack.split(/\n\n/).pop()
    trace = trace.replace(/(^|\n)([ \t]+)?at /g, '$1')
    trace = trace.split('\n').map((traceLine, i) => {
        let tmpTraceLine = traceLine.match(/(^[^ ]+|\(([^)]+)\))/ig)
        if (tmpTraceLine && tmpTraceLine[1]) {
            traceLine = tmpTraceLine
            traceLine[1] = traceLine[1].substr(1, traceLine[1].length - 2).split(':')
            let func = traceLine[0].padEnd(40, ' ')
            let line = traceLine[1][1]
            let col = traceLine[1][2]
            let file = `${traceLine[1][0]}:${line}:${col}`
            let output = `  -> ${func}${colors.bold('Line:')} ${line}   ${colors.bold('Col:')} ${col}\n  ${file}`
            // let's emphasize the last step in the stacktrace
            if (i === 0) {
                output = colors.bold(output)
            }
            return output
        }
        return traceLine
    })
}

function removeBackTick (str) {
    return str.replace('\'', '')
}

async function getFileFromErrorObject (err, removePath = '', ref, opts) {
    // let file = err.message.match(FILE_RX)
    let file
    let colLine = ''

    if (err.error && err.error.error) {
        file = err.error.error.message.match(FILE_RX)
        colLine = err.error.error.message.match(LINE_COL_RX)
    }
    // if file was not found in the error message itself
    if (!file) {
        // some errors have a second level or error for children
        if (err.error) {
            file = err.error.message.match(FILE_RX)
            colLine = colLine || err.error.message.match(LINE_COL_RX)
        }
        
        // if still don't have the file...
        if (!file){
            if (err.message) {
                file = err.message.match(FILE_RX)
                colLine = colLine || err.message.match(LINE_COL_RX)
            } else {
                file = err.toString().match(FILE_RX)
                colLine = colLine || err.toString().match(LINE_COL_RX)
            }
        }
    }
    let couldntResolveModule = (err.message || err.toString()).match(MOD_NOT_FOUND)
    if (opts.mode === 'dev' &&
        file[1] &&
        (file[0].startsWith('.') || file[0].startsWith('/')) &&
        couldntResolveModule &&
        opts.development.autoCreateFiles !== false) {

        let shallCreate = false
        if (opts.development.autoCreateFiles === 'auto') {
            shallCreate = true
        }

        if (opts.development.autoCreateFiles === 'interactive') {
            // ask if we should create the file
            await new Promise((resolve, reject) => {
                ref.spinner.stop()
                clearTerminal()
                inquirer.prompt([{
                    type: 'list',
                    name: 'createFile',
                    message: colors.reset('\n' +
                        `Your file ${colors.greenBright(file[1].replace(removePath, '').replace(/^ +/g, ''))}\n` +
                        `is imporing ${colors.redBright(file[0].replace(removePath, '').replace(/^ +/g, ''))},\n` +
                        `But it does not exist!\n`) +
                        colors.bold(`Should we create that for you?`),
                    choices: ['yes', 'no']
                }]).then(answers => {
                    if (answers.createFile === 'yes') {
                        shallCreate = true
                    } else {
                        file = removeBackTick(file[0])
                    }
                    resolve()
                })
            })
        }
        if (shallCreate) {
            file[0] = removeBackTick(file[0])
            file[1] = removeBackTick(file[1])
            
            let newFile = path.resolve(
                file[1],
                file[0]
            )
            setTimeout(_ => {
                fs.ensureFile(newFile)
            }, 1000)
            file = file[1]

            return ({ newFile, ignoreError: true, file, toString () { return this.file } })
        }
    } else {
        file = removeBackTick(file[0])
    }
    // time to check for the collumn and line
    if (colLine) {
        colLine = colLine[0]
        colLine = colLine.replace(/\[\d+m/g, '')
        let tmpColLine = colLine.match(/(\d+)[^\d]+(\d+)/)
        if (tmpColLine) {
            colLine = {
                line: tmpColLine[1],
                col: tmpColLine[2],
                toString: function () {
                    return `Line: ${this.line} | Col: ${this.col}\n`
                }
            }
        } else {
            colLine += '\n'
        }
    } else {
        colLine = '\n'
    }
    return {
        file: file
            ? file.replace(removePath, '').replace(/^ +/g, '')
            : '',
        colLine,
        toString: function () {
            if (this.file) {
                return `File: ${colors.italic(this.file)}    ${this.colLine}${reset()}`
            }
            return this.colLine.toString() || ''
        }
    }
}

async function treatSoftErrors (stats, ref, opts) {
    const PATHS = ref.paths
    let errList = []
    let erroredFiles = []

    // stats.stats.forEach(async stat => {
    for (let stat in stats.stats) {
        stat = stats.stats[stat]
        // errList = errList.concat(stat.compilation.errors.map(async err => {
        for (let err of stat.compilation.errors) {
            let errMessage = ''
            let fileData = {}
            
            if (typeof err === 'string') {
                if (stat.compilation.errors.length && err.indexOf('Child compilation') < 0) {
                    // error compiling
                    fileData = await getFileFromErrorObject(err, PATHS.project, ref, opts)
                    let msg = err.message || err.toString()
                    msg = msg.replace(PATHS.project + (fileData.file || ''), '')
                    msg = `${fileData}${reset()}${msg}${err.codeFrame ? '\n' + err.codeFrame + '\n' : ''}${reset()}`
                    errMessage = msg
                } else {
                    // is a child's error
                    errMessage = stat.compilation.children.map(async child => {
                        // return await child.errors.map(async err => {
                        let ret = []
                        for (err of child.errors) {
                            fileData = await getFileFromErrorObject(err, PATHS.project, ref, opts)
                            let msg = err.message || err.toString()
                            msg = msg.replace(PATHS.project + (fileData.file || ''), '')
                            msg = `${fileData}${reset()}${msg}${err.codeFrame ? '\n' + err.codeFrame + '\n' : ''}${colors.reset()}`
                            ret.push(msg)
                        }
                        ret = ret.join('\n')
                    }).join('\n').replace(/^\n/, '')
                }
            } else {
                // this is a compilation error
                let theError = err.error
                if (theError) {
                    theError = theError.error || theError
                } else {
                    theError = err
                }
                let fullMsg
                // let theError = err.error.error || err.error
                if (err.message.split('\n').length > theError.message.split('\n').length) {
                    fullMsg = err.message
                } else {
                    fullMsg = theError.message
                }
                // clean it up (remove stacktrace in case it was added to the message)
                fullMsg = fullMsg.replace(/\n(\t| +)(at|@) [^\n]+/gm, '')

                // let fullMsg = theError.message.replace(/^[\t\n\r ]+/g, '')      // Alternative A
                // let fullMsg = err.message.replace(/^[\t\n\r ]+/g, '')        // Alternative B
                                                                                // Alternative C (same as B, but not using the codeframe in the end)
                fileData = await getFileFromErrorObject(err, PATHS.project, ref, opts)

                // let file = fullMsg.substr(0, fullMsg.indexOf(':'))
                // file = file || fullMsg.substr(0, fullMsg.indexOf('\n'))
                // removing the repetitive portion of the path
                // file = file ? file.shift()
                // let message = fullMsg.substr(fullMsg.indexOf(' '))
                let message = fullMsg.replace(PATHS.project + (fileData.file || ''), '')
                let name = theError.name
                let codeFrame = theError.codeFrame
                codeFrame = codeFrame ? `\n\n${codeFrame}` : ''

                errMessage = `${colors.bold(name) }\n${fileData}${reset()}${message}${codeFrame}${reset()}`
            }
            if (fileData && fileData.file) {
                erroredFiles.push(fileData.file)
            }
            if (fileData.ignoreError) {
                errList.push(`[i] > Module ${fileData.file} not found.\nThis file will be created at ${fileData.newFile}`)
            } else {
                errList.push(errMessage)
            }
            // return errMessage
        }
    }
    let len = errList.length
    errList.forEach((err, i) => {
        errList[i] = colors.bold.red('\n' + symbols.error + (len > 1 ? (i + 1) + ') ' : '')) + errList[i]
    })
    return {
        erroredFiles,
        messages: errList
    }
}

function treatWarnings (warnsList, PATHS, opts) {
    let warns = []
    let infos = []
    
    // we have to treat each warning message
    warnsList.forEach((warn, i) => {
        // if there is more than one, we add a counter
        
        // we can clean a little more, the warning messages
        if (warn.replace) {
            warn = warn.replace(/^\(undefined\) /, '')
            // and if it is a recomendation, we change it to type info
            if (warn.match(/webpack [a-z0-9_\-\.]+ recommendations\:/)) {
                infos.push(infoMessage(warn))
            } else {
                warns.push(warnMessage(warn))
            }
        }
    })
    
    return {
        warns,
        infos
    }
}

export { treatSoftErrors, treatWarnings }



// WEBPACK FOOTER //
// ./src/handlers.js